import React, { useEffect, useState, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const FALLBACK = [
  { src: "https://images.pexels.com/photos/34079355/pexels-photo-34079355.jpeg?auto=compress&cs=tinysrgb&w=1200", category: "Wedding" },
  { src: "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", category: "Light Decoration" },
  { src: "https://images.unsplash.com/photo-1764380746818-18c01e96df12?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", category: "Birthday" },
  { src: "https://images.unsplash.com/photo-1593294353764-f76527c3b853?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", category: "Varmala Entry" },
  { src: "https://images.unsplash.com/photo-1570102881689-c04ab4cf1f4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", category: "Baby Shower" },
  { src: "https://images.pexels.com/photos/33485972/pexels-photo-33485972.jpeg?auto=compress&cs=tinysrgb&w=1200", category: "Wedding" },
];

export default function GalleryPage() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [lb, setLb] = useState(-1);
  const touchX = useRef(null);

  useEffect(() => {
    api.get("/gallery").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const images = items.length
    ? items.map((g) => ({ src: fileUrl(g.storage_path), caption: g.caption, category: g.category || "Other" }))
    : FALLBACK.map((f) => ({ ...f, caption: "" }));

  const cats = ["All", ...Array.from(new Set(images.map((i) => i.category).filter(Boolean)))];
  const groups = cats.filter((c) => c !== "All").map((c) => ({ c, imgs: images.filter((i) => i.category === c) })).filter((g) => g.imgs.length);
  const flat = cat === "All" ? groups.flatMap((g) => g.imgs) : images.filter((i) => i.category === cat);

  const close = useCallback(() => setLb(-1), []);
  const next = useCallback(() => setLb((i) => (i + 1) % flat.length), [flat.length]);
  const prev = useCallback(() => setLb((i) => (i - 1 + flat.length) % flat.length), [flat.length]);

  useEffect(() => {
    if (lb < 0) return;
    const onKey = (e) => { if (e.key === "Escape") close(); if (e.key === "ArrowRight") next(); if (e.key === "ArrowLeft") prev(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lb, close, next, prev]);

  const Figure = ({ img, idx, testid }) => (
    <figure
      className="relative rounded-2xl overflow-hidden border border-pink-200 group cursor-pointer"
      data-testid={testid}
      onClick={() => setLb(idx)}
    >
      <img src={img.src} alt={img.caption || img.category} className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
      {img.caption && <figcaption className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-sm text-white">{img.caption}</figcaption>}
    </figure>
  );

  let counter = -1;

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
      <div className="text-center mb-10">
        <span className="text-xs tracking-[0.2em] uppercase text-gold">{t("gallery_title")}</span>
        <h1 className="font-display text-4xl sm:text-5xl text-zinc-900 mt-3">{t("nav_gallery")}</h1>
        <p className="text-zinc-600 mt-4">{t("gallery_sub")}</p>
      </div>

      <div className="flex flex-wrap justify-center gap-2 mb-10">
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`gallery-cat-${c}`}
            className={`px-5 py-2 rounded-full text-sm transition-colors border ${cat === c ? "gold-gradient text-white border-transparent font-semibold" : "border-pink-300 text-zinc-700 hover:border-gold/60"}`}>
            {c}
          </button>
        ))}
      </div>

      {cat === "All" ? (
        groups.map((g) => (
          <section key={g.c} className="mb-14" data-testid={`gallery-section-${g.c}`}>
            <h2 className="font-display text-2xl text-zinc-900 mb-5 flex items-center gap-3">
              <span className="h-px flex-1 bg-pink-200" /> {g.c} <span className="h-px flex-1 bg-pink-200" />
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {g.imgs.map((img) => { counter += 1; return <Figure key={counter} img={img} idx={counter} testid={`gallery-item-${g.c}-${counter}`} />; })}
            </div>
          </section>
        ))
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {flat.map((img, i) => <Figure key={i} img={img} idx={i} testid={`gallery-filtered-${i}`} />)}
        </div>
      )}

      {lb >= 0 && flat[lb] && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          data-testid="lightbox"
          onClick={close}
          onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
          onTouchEnd={(e) => {
            if (touchX.current == null) return;
            const dx = e.changedTouches[0].clientX - touchX.current;
            if (dx > 50) prev(); else if (dx < -50) next();
            touchX.current = null;
          }}
        >
          <button onClick={close} data-testid="lightbox-close" className="absolute top-5 right-5 text-white/80 hover:text-white"><X size={30} /></button>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev" className="absolute left-3 sm:left-8 text-white/70 hover:text-white bg-white/10 rounded-full p-2"><ChevronLeft size={28} /></button>
          <figure className="max-w-5xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img src={flat[lb].src} alt={flat[lb].caption || ""} className="max-h-[80vh] w-auto mx-auto rounded-xl object-contain" />
            <figcaption className="text-center text-white/80 mt-3 text-sm">{flat[lb].caption || flat[lb].category} · {lb + 1} / {flat.length}</figcaption>
          </figure>
          <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next" className="absolute right-3 sm:right-8 text-white/70 hover:text-white bg-white/10 rounded-full p-2"><ChevronRight size={28} /></button>
        </div>
      )}
    </div>
  );
}
